import { useState, useEffect } from "react";
import { Play, Pause, StopCircle, ChevronDown } from "lucide-react";
import { Card, CardContent, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { enqueueSnackbar } from "notistack";
import moment from "moment";
import * as patrulhasSessoesService from "../services/patrulhasSessoes";
import type { PatrulhaSessao } from "../services/patrulhasSessoes";

interface SessaoCardProps {
  onSessaoChange?: () => void;
}

export function SessaoCard({ onSessaoChange }: SessaoCardProps) {
  const [sessao, setSessao] = useState<PatrulhaSessao | null>(null);
  const [loading, setLoading] = useState(false);
  const [tempoAtual, setTempoAtual] = useState(0);
  const [historicoAberto, setHistoricoAberto] = useState(false);
  const [modalPausarAberta, setModalPausarAberta] = useState(false);
  const [modalFinalizarAberta, setModalFinalizarAberta] = useState(false);

  useEffect(() => {
    carregarSessao();
  }, []);

  useEffect(() => {
    if (!sessao || sessao.status === "fechada") return;

    const interval = setInterval(() => {
      calcularTempoAtual();
    }, 1000);

    return () => clearInterval(interval);
  }, [sessao]);

  const carregarSessao = async () => {
    try {
      const data = await patrulhasSessoesService.obterSessaoAtiva();
      setSessao(data);
      if (data) {
        calcularTempoAtual();
      }
    } catch (error: any) {
      console.error("Erro ao carregar sessão:", error);
    }
  };

  const calcularTempoAtual = () => {
    if (!sessao) return;

    const inicio = moment(sessao.data_hora_inicio);
    const agora = moment();
    let segundosDecorridos = agora.diff(inicio, "seconds");

    // Se está pausada, calcula até o momento da pausa
    if (sessao.status === "pausada" && sessao.data_hora_pausa) {
      const pausa = moment(sessao.data_hora_pausa);
      segundosDecorridos = pausa.diff(inicio, "seconds");
    }

    // Subtrai o tempo pausado acumulado
    segundosDecorridos -= sessao.tempo_pausado_segundos;

    setTempoAtual(Math.max(0, segundosDecorridos));
  };

  const formatarTempo = (segundos: number) => {
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    const segs = segundos % 60;
    return `${String(horas).padStart(2, "0")}:${String(minutos).padStart(2, "0")}:${String(segs).padStart(2, "0")}`;
  };

  const handleAbrir = async () => {
    setLoading(true);
    try {
      const novaSessao = await patrulhasSessoesService.abrirSessao();
      setSessao(novaSessao);
      enqueueSnackbar("Sessão de patrulha aberta com sucesso!", { variant: "success" });
      onSessaoChange?.();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao abrir sessão", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePausar = async () => {
    setLoading(true);
    try {
      const sessaoAtualizada = await patrulhasSessoesService.pausarSessao();
      setSessao(sessaoAtualizada);
      setModalPausarAberta(false);
      enqueueSnackbar("Sessão pausada", { variant: "info" });
      onSessaoChange?.();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao pausar sessão", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRetomar = async () => {
    setLoading(true);
    try {
      const sessaoAtualizada = await patrulhasSessoesService.retomarSessao();
      setSessao(sessaoAtualizada);
      enqueueSnackbar("Sessão retomada", { variant: "success" });
      onSessaoChange?.();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao retomar sessão", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizar = async () => {
    setLoading(true);
    try {
      const sessaoFinalizada = await patrulhasSessoesService.finalizarSessao();
      setSessao(sessaoFinalizada);
      setModalFinalizarAberta(false);
      enqueueSnackbar("Sessão finalizada com sucesso!", { variant: "success" });
      onSessaoChange?.();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao finalizar sessão", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = () => {
    if (!sessao) return null;

    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      aberta: "default",
      pausada: "secondary",
      fechada: "destructive",
    };

    return (
      <Badge variant={variants[sessao.status] || "default"}>
        {sessao.status === "aberta" ? "Aberta" : sessao.status === "pausada" ? "Pausada" : "Fechada"}
      </Badge>
    );
  };

  return (
    <Card className="py-0">
      <CardContent className="py-3 space-y-1">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Sessão</CardTitle>
          {sessao && getStatusBadge()}
        </div>
        {!sessao ? (
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Nenhuma sessão ativa</p>
            <Button onClick={handleAbrir} disabled={loading} size="sm">
              <Play className="mr-1 h-3 w-3" />
              Abrir
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <span className="text-lg font-mono font-bold">{formatarTempo(tempoAtual)}</span>
              <div className="flex gap-2">
                {sessao.status === "aberta" && (
                  <>
                    <Button onClick={() => setModalPausarAberta(true)} disabled={loading} variant="outline" size="sm">
                      <Pause className="h-4 w-4" />
                    </Button>
                    <Button onClick={() => setModalFinalizarAberta(true)} disabled={loading} variant="destructive" size="sm">
                      <StopCircle className="h-4 w-4" />
                    </Button>
                  </>
                )}
                {sessao.status === "pausada" && (
                  <>
                    <Button onClick={handleRetomar} disabled={loading} size="sm">
                      <Play className="h-4 w-4" />
                    </Button>
                    <Button onClick={() => setModalFinalizarAberta(true)} disabled={loading} variant="destructive" size="sm">
                      <StopCircle className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Dropdown de Histórico */}
            {sessao.logs && sessao.logs.length > 0 && (
              <Collapsible open={historicoAberto} onOpenChange={setHistoricoAberto}>
                <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium hover:underline">
                  Histórico ({sessao.logs.length})
                  <ChevronDown className={`h-3 w-3 transition-transform ${historicoAberto ? "rotate-180" : ""}`} />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-1">
                  <div className="space-y-1 max-h-24 overflow-y-auto border rounded-md p-1.5">
                    {sessao.logs.map((log) => (
                      <div key={log.id} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium">{log.militar.patente.sigla} {log.militar.nick}</span>
                          <span className="text-muted-foreground">{log.acao}</span>
                        </div>
                        <span className="text-muted-foreground">
                          {moment(log.data_hora).format("HH:mm")}
                        </span>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </>
        )}
      </CardContent>

      {/* Modal de Confirmação de Pausa */}
      <Dialog open={modalPausarAberta} onOpenChange={setModalPausarAberta}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pausar Sessão</DialogTitle>
            <DialogDescription>
              Deseja realmente pausar a sessão de patrulha? O tempo será pausado até que a sessão seja retomada.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalPausarAberta(false)}>
              Cancelar
            </Button>
            <Button onClick={handlePausar} disabled={loading}>
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Finalização */}
      <Dialog open={modalFinalizarAberta} onOpenChange={setModalFinalizarAberta}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Finalizar Sessão</DialogTitle>
            <DialogDescription>
              Deseja realmente finalizar a sessão de patrulha? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalFinalizarAberta(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleFinalizar} disabled={loading}>
              Finalizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
